export interface SlotAvailability {
  slot_id: number;
  date: string;
  start_time: string;
  end_time: string;
  resource_type: "stol" | "kruh" | "workshop";
  capacity: number;
  title: string | null;
  price_cents: number;
  available: number;
  booked: number;
  is_active?: boolean; // only present in admin view
  workshop_kind?: WorkshopKindID; // workshops only
  clayground: boolean; // instructor present during this slot
}

/** An upcoming workshop a workshop voucher can be used on (POST /api/me/vouchers/redeem). */
export interface WorkshopOption {
  slot_id: number;
  date: string;
  start_time: string;
  end_time: string;
  title: string;
  available: number;
}

/**
 * Result of POST /api/me/vouchers/redeem. `type` tells the shape:
 * credit → amount/balance; workshop with `options` → nothing happened yet, pick one;
 * workshop with `booked` → the seat is booked and the voucher is used up.
 */
export interface RedeemVoucherResult {
  type: "credit" | "workshop";
  code: string;
  amount_cents?: number;
  balance_cents?: number;
  workshop_kind?: WorkshopKindID;
  workshop_kind_label?: string;
  options?: WorkshopOption[];
  booking_group_id?: string;
  booked?: WorkshopOption;
}

/** Workshop kinds live in the API (api/workshop); the id is what slots and vouchers store. */
export type WorkshopKindID = "glaze_chill" | "clay_wine" | "roztoc_to";

export interface WorkshopKind {
  id: WorkshopKindID;
  label: string;
  default_price_cents: number;
}

export interface Booking {
  id: number;
  slot_id: number;
  user_id: string;
  customer_code: string;
  status: "confirmed" | "cancelled" | "pending_payment";
  created_at: string;
  num_persons: number;
  booking_group_id: string;
  checkout_url?: string;
  pin_status: "" | "active" | "revoked"; // empty string if no lock_pins row yet
  /** What this row costs: slot price × persons, after discount. */
  booked_price_cents: number;
  discount_code?: string;
  slots?: {
    date: string;
    start_time: string;
    end_time: string;
    resource_type: string;
    title: string | null;
    price_cents: number;
  };
}

export interface SlotBookingInfo {
  id: number;
  status: string;
  created_at: string;
  customer_code: string;
  email: string;
  name: string;
  num_persons: number;
  booking_group_id: string;
  group_slot_count: number;
  pin_status: "" | "active" | "revoked";
  discount_code?: string;
  /** Active PIN, so the admin can help a customer at the door (valid 15 min before to 15 min after). */
  pin?: string;
}

export interface CreditTransactionBooking {
  resource_type: ResourceType;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  num_persons: number;
}

export interface CreditTransaction {
  id: number;
  amount_cents: number;
  type: string;
  description: string;
  created_at: string;
  /** Súhrn prepojenej rezervácie; chýba pri úpravách adminom bez rezervácie. */
  booking?: CreditTransactionBooking;
}

export interface Customer {
  id: number;
  user_id: string;
  customer_code: string;
  name: string;
  email: string;
  created_at: string;
}

export interface AdminVoucher {
  id: number;
  code: string;
  type: "credit" | "workshop";
  value_cents: number;
  status: "active" | "redeemed" | "void";
  source: "online" | "offline";
  buyer_email?: string;
  /** Workshop vouchers only: the kind this voucher is one seat on. */
  workshop_kind?: WorkshopKindID;
  workshop_kind_label?: string;
  note?: string;
  expires_at?: string;
  created_at: string;
  redeemed_at?: string;
  voided_at?: string;
  redeemed_by?: { name: string; email: string; customer_code: string };
  /** A designer PDF template exists for this value; the download button is shown. */
  pdf_available: boolean;
}

export type ResourceType = "stol" | "kruh" | "workshop";

export const RESOURCE_LABELS: Record<ResourceType, string> = {
  stol: "Miesto pri stole",
  kruh: "Hrnčiarsky kruh",
  workshop: "Workshop",
};

export const DAY_NAMES = [
  "Nedeľa",
  "Pondelok",
  "Utorok",
  "Streda",
  "Štvrtok",
  "Piatok",
  "Sobota",
];

export const DAY_NAMES_SHORT = ["Ne", "Po", "Ut", "St", "Št", "Pi", "So"];

/** One row of the admin discount code list (GET /api/admin/discount-codes). */
export interface AdminDiscountCode {
  id: number;
  code: string;
  /** Exactly one is set: percent off every hour, or amount_cents off the first hour (once per customer). */
  percent?: number;
  amount_cents?: number;
  expires_at?: string;
  /** null = unlimited */
  max_uses: number | null;
  is_active: boolean;
  note?: string;
  /** Bookings made with the code; cancelled ones don't count. */
  uses: number;
  /** Switched on, not expired and under its limit: a customer can apply it now. */
  usable: boolean;
  created_at: string;
}

/** The editable fields of a discount code; the code and percentage are fixed. */
export interface DiscountCodeLimits {
  expires_at: string;
  max_uses: number | null;
  is_active: boolean;
  note: string;
}
